import { TeamMember } from '@dynamika-shared/types/teamMember';
import { TeamRoles } from 'src/constants/rolesList';

const SCOPE_ALL_KEYWORD = 'all';

/**
 * Parse a raw `siteCodes` string (as returned for a TeamMember) into a list of
 * normalized site codes. The backend may separate codes with `;` or `,`. The
 * synthetic "all" entry is dropped — it is not a real site code.
 */
export const parseSiteCodes = (
  siteCodes: string | undefined | null
): string[] => {
  if (!siteCodes) return [];
  return siteCodes
    .split(/[;,]/)
    .map(code => code.trim())
    .filter(code => code && code.toLowerCase() !== SCOPE_ALL_KEYWORD);
};

/**
 * The set of site codes a subscription recipient may be scoped to.
 *
 * Site users (`TeamRoles.SITE`) are hard-limited to the sites they belong to;
 * every other recipient (other roles, or external email recipients with no
 * team-member record) is unrestricted. `null` means "no restriction — any CT
 * site is allowed".
 *
 * A site user with no `siteCodes` yields an empty set: the scope dropdown
 * offers nothing and the selection is pruned to `[]`. (In practice a site user
 * always has at least one associated site, so this is a defensive edge case.)
 */
export const getAllowedSiteCodes = (
  member: Pick<TeamMember, 'roles' | 'siteCodes'> | undefined | null
): Set<string> | null => {
  if (member?.roles !== TeamRoles.SITE) return null;
  return new Set(parseSiteCodes(member.siteCodes));
};

/**
 * Clamp a list of selected site codes to those allowed for the recipient.
 * When the recipient is unrestricted (`allowed === null`) the selection is
 * returned unchanged.
 */
export const clampSiteCodes = (
  selected: string[],
  allowed: Set<string> | null
): string[] => {
  if (!allowed) return selected;
  return selected.filter(code => allowed.has(code));
};

/**
 * Resolve the site codes to persist for a subscription recipient at submit
 * time.
 *
 * - Unrestricted recipient (`allowed === null`): the selection is used as-is;
 *   an empty selection means "all sites" (backend wildcard convention).
 * - Site user (`allowed` is a set): an empty selection is expanded to the
 *   user's full set of associated sites, so the subscription is stored with an
 *   explicit site array instead of the "all sites" wildcard. A non-empty
 *   selection is clamped to the allowed set.
 */
export const resolveRecipientScope = (
  selected: string[],
  allowed: Set<string> | null
): string[] => {
  if (!allowed) return selected;
  if (selected.length === 0) return [...allowed];
  return clampSiteCodes(selected, allowed);
};
